package com.essntl.features.supplier.data.repository

import com.essntl.core.supabase.*
import com.essntl.core.utils.repository.Repository
import com.essntl.features.supplier.data.supabase.SupplierDto
import com.essntl.features.supplier.data.supabase.SupplierSupabaseDataSource
import com.essntl.features.supplier.data.supabase.toRequestMap
import com.essntl.features.supplier.domain.model.SupplierFilterOptions
import com.essntl.features.supplier.domain.model.SupplierModel
import com.essntl.features.supplier.domain.repository.SupplierRepository
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.PostgrestFilterBuilder
import org.koin.core.annotation.Single

@Single(binds = [SupplierRepository::class])
internal class SupplierRepositoryImpl internal constructor(
    private val supabase: SupplierSupabaseDataSource,
) : Repository(), SupplierRepository {
    override suspend fun getAll(): Result<List<SupplierModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = SupplierDto.getColumns(),
                )
                .map {
                    it.toModel()
                }
        }

    override suspend fun getSuppliersByContacts(
        contactIds: List<String>,
    ): Result<List<SupplierModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = SupplierDto.getColumns(),
                ) {
                    filter {
                        if (contactIds.isNotEmpty()) {
                            or {
                                contactIds.forEach { id ->
                                    eq("contact_id", id)
                                }
                            }
                        }
                    }
                }
                .map {
                    it.toModel()
                }
        }

    override suspend fun getAll(
        page: Long,
        limit: Int,
        filterOptions: SupplierFilterOptions,
    ): Result<List<SupplierModel>> =
        runCatching {
            supabase
                .getAll(
                    page = page,
                    limit = limit,
                    columns = SupplierDto.getColumns(),
                ) {
                    if (
                        filterOptions.searchText.isNotEmpty() ||
                        filterOptions.selectedCategoryIds.isNotEmpty() ||
                        filterOptions.selectedAreaIds.isNotEmpty()
                    ) {
                        filter {
                            if (filterOptions.searchText.isNotEmpty()) {
                                ilike("name", "%${filterOptions.searchText}%")
                                and {
                                    applyCategoryArea(filterOptions)
                                }
                            } else {
                                and {
                                    applyCategoryArea(filterOptions)
                                }
                            }
                        }
                    }

                    order("created_at", Order.DESCENDING)
                }
                .map { it.toModel() }
        }

    override suspend fun getByIds(ids: List<String>): Result<List<SupplierModel>> =
        runCatching {
            val supplierList =
                supabase.getByIds(
                    ids = ids,
                    columns = SupplierDto.getColumns(),
                )

            supplierList
                .map { supplier ->
                    supplier.toModel()
                }
        }

    private fun PostgrestFilterBuilder.applyCategoryArea(filterOptions: SupplierFilterOptions) {
        if (filterOptions.selectedCategoryIds.isNotEmpty()) {
            and {
                or {
                    filterOptions.selectedCategoryIds.forEach { categoryId ->
                        eq("category", categoryId)
                    }
                }
            }
        }

        if (filterOptions.selectedAreaIds.isNotEmpty()) {
            and {
                or {
                    filterOptions.selectedAreaIds.forEach { areaId ->
                        eq("area", areaId)
                    }
                }
            }
        }
    }

    override suspend fun get(id: String): Result<SupplierModel> =
        runCatching {
            supabase
                .getById(
                    id = id,
                    columns = SupplierDto.getColumns(),
                )
                .toModel()
        }

    override suspend fun getCountSuppliersByCategory(categoryId: String): Result<Long> =
        runCatching {
            supabase.getCount {
                filter {
                    eq("category", categoryId)
                }
            }
        }

    override suspend fun getCountSuppliersByArea(areaId: String): Result<Long> =
        runCatching {
            supabase.getCount {
                filter {
                    eq("area", areaId)
                }
            }
        }


    override suspend fun insert(model: SupplierModel): Result<SupplierModel> =
        runCatching {
            val requestMap = model.toRequestMap()
            supabase
                .insert(
                    requestMap = requestMap,
                    columns = SupplierDto.getColumns(),
                )
                .toModel()
        }

    override suspend fun update(model: SupplierModel): Result<SupplierModel> =
        runCatching {
            val requestMap = model.toRequestMap()
            supabase
                .update(
                    id = model.id,
                    requestMap = requestMap,
                    columns = SupplierDto.getColumns(),
                ).toModel()
        }

    override suspend fun delete(model: SupplierModel): Result<Unit> =
        runCatching {
            supabase
                .delete(model.id)
        }

    override suspend fun getByPhoneNumbers(phoneNumbers: Set<String>): Result<List<SupplierModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = SupplierDto.getColumns()
                ) {
                    filter {
                        if (phoneNumbers.isNotEmpty()) {
                            or {
                                phoneNumbers.forEach { phoneNumber ->
                                    eq("telephone", phoneNumber)
                                }
                            }
                        }
                    }
                }
                .map { it.toModel() }
        }

    override suspend fun getByContactId(contactId: String): Result<SupplierModel?> =
        runCatching {
            supabase
                .getOne(
                    columns = SupplierDto.getColumns(),
                ) {
                    filter {
                        eq("contact_id", contactId)

                    }
                }?.toModel()
        }
}
